// SPDX-License-Identifier: Elastic-2.0
/**
 * DashboardPage — content of `/dashboard` (T-020).
 *
 * Slim by design: composes two sibling features that live in the same
 * folder:
 *
 *   1. `<SuggestionStrip />` (T-022) — pending suggestion summary strip
 *      at the top.
 *   2. `<DeviceCardsByRoom />` (T-021) — devices grouped by room.
 *
 * The persistent chrome (sidebar + topbar) is provided by
 * `<DashboardLayout>` via the route tree — this component renders inside
 * its `<Outlet />`.
 *
 * Lazy + Suspense rationale
 * ─────────────────────────
 * T-021 and T-022 are sibling agents building these two components
 * concurrently with T-020. To avoid a build-time failure when one
 * sibling hasn't landed yet (CI race during the parallel group), we
 * pull them in via `React.lazy` + `<Suspense>`. The runtime cost is one
 * extra microtask per first render — negligible.
 *
 * Once both siblings have stabilised the import can become eager again
 * by replacing `lazy(() => import(...))` with a static `import`.
 *
 * Tests in isolation: when the sibling files don't exist yet, the lazy
 * import rejects. Tests for this page should mock the lazy chunks via
 * `vi.mock("./SuggestionStrip", ...)` before importing `DashboardPage`
 * — see `DashboardPage.test.tsx` for the pattern.
 *
 * Empty / loading states: handled by each child individually (the
 * Suspense fallback is a single page-level skeleton; per-section loading
 * lives in the children).
 */
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Suggestion } from "@/api/gateway/models/Suggestion";

import { Page } from "../../components/Page/Page";
import { DeviceModal } from "../device-control/DeviceModal";
import { SuggestionApprovalModal } from "../suggestions/SuggestionApprovalModal";

import { HeroCard } from "./HeroCard";
import { HomeGraph } from "./HomeGraph";

import styles from "./DashboardPage.module.css";

// Sibling lazy chunks. Path-only — these files are owned by T-022 and
// T-021 respectively. SuggestionStrip default-exports its root; T-021's
// DeviceCardsByRoom (current revision) only ships a named export so we
// re-shape it into the `{ default }` contract `React.lazy` expects.
// If neither has landed, the Suspense fallback paints; tests mock the
// modules to isolate this page.
const SuggestionStrip = lazy(() => import("./SuggestionStrip"));
const DeviceCardsByRoom = lazy(async () => {
  const mod = await import("./DeviceCardsByRoom");
  // Support both default-export and named-export shapes so we don't
  // break if T-021 later adds a default export.
  const Component =
    (mod as { default?: typeof mod.DeviceCardsByRoom }).default ?? mod.DeviceCardsByRoom;
  return { default: Component };
});

/** Page-level loading skeleton while sibling chunks resolve. */
function PageSkeleton(): JSX.Element {
  return (
    <div className={styles.stack} data-testid="dashboard-page-skeleton">
      <div className={styles.skeleton} aria-hidden="true" />
      <div className={styles.skeleton} style={{ minHeight: 240 }} aria-hidden="true" />
    </div>
  );
}

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  // T-025: own which suggestion is showing in the approval modal. The
  // strip hands us the suggestion via `onAccept`; we mount the modal at
  // the page level (one instance, regardless of how many strips/cards
  // might trigger it later). `null` = closed.
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const handleCloseSuggestion = useCallback(() => setSelectedSuggestion(null), []);
  // T-024: same shape for the device-control modal. We keep just the
  // deviceId (DeviceModal resolves the Device from the list cache so the
  // grid and the modal stay in lockstep when WS pushes mutate state).
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const handleCloseDevice = useCallback(() => setSelectedDeviceId(null), []);

  // T-029: bump the visit counter the PWA install prompt reads. We tick
  // once per mount (route changes count) so the threshold fires after
  // ~3 separate visits, not after 3 re-renders inside one session.
  useEffect(() => {
    try {
      const current = Number(localStorage.getItem("dashboard-visit-count") ?? 0);
      localStorage.setItem("dashboard-visit-count", String(current + 1));
    } catch {
      // Storage unavailable — the install prompt simply never fires.
    }
  }, []);

  return (
    <Page title={t("dashboard.title")} data-testid="dashboard-page">
      <Suspense fallback={<PageSkeleton />}>
        <div className={styles.stack}>
          {/* Hero greets the user + summarises status + offers scenes.
              Designed against ui_kits/dashboard/B1Home.jsx — the orb
              now lives inside this card, not behind the whole page. */}
          <HeroCard />
          <SuggestionStrip onAccept={setSelectedSuggestion} />
          {/* "Mapa del hogar" — top-down floor plan between
              suggestions and device cards, matching B1Home design.
              Hardcoded fixture rooms for v1; real F-300 home-graph
              endpoint deferred. */}
          <HomeGraph />
          <DeviceCardsByRoom onOpenDevice={setSelectedDeviceId} />
        </div>
      </Suspense>
      <SuggestionApprovalModal
        suggestion={selectedSuggestion}
        open={selectedSuggestion !== null}
        onClose={handleCloseSuggestion}
      />
      <DeviceModal deviceId={selectedDeviceId} onClose={handleCloseDevice} />
    </Page>
  );
}

export default DashboardPage;
